/**
 * Talk it through's first step: the service-account keys a solution's scripts
 * load (`services/<key file>`) to call Gemini and search the knowledge base.
 *
 * Given up front, they let creation finish the job the backend upload can't:
 * the keys go into the new solution's `services/` (so its first chat doesn't
 * fail for want of them), and the attached references are built into a
 * Vertex RAG corpus in one key's project, which the Knowledge Agent's search
 * node and script are then pointed at. See `run-knowledge-setup.ts`.
 *
 * Keys live in memory only, for the length of the handoff from Talk it
 * through to the new solution — never in browser storage.
 */

export interface KeyFile {
  /** The file name, kept: the scripts load the key by it. */
  name: string;
  /** The file's contents (JSON, whatever the extension). */
  text: string;
  projectId: string;
  clientEmail: string;
}

export const readKeyFile = (
  name: string,
  text: string
): KeyFile | { error: string } => {
  let v: Record<string, unknown> | null = null;
  try {
    v = JSON.parse(text) as Record<string, unknown>;
  } catch {
    // fall through
  }
  if (
    !v ||
    v.type !== 'service_account' ||
    typeof v.project_id !== 'string' ||
    typeof v.client_email !== 'string' ||
    typeof v.private_key !== 'string'
  ) {
    return { error: `${name} isn't a credential file Pro Studio can read.` };
  }
  return {
    name,
    text,
    projectId: v.project_id,
    clientEmail: v.client_email,
  };
};

/**
 * The search script pointed at the corpus's project and key file, or null
 * when it already is. Only the two constants change; the script is otherwise
 * the template's.
 */
export const retargetRagScript = (
  script: string,
  { projectId, keyFileName }: { projectId: string; keyFileName: string }
): string | null => {
  const next = script
    .replace(
      /^(\s*PROJECT_ID\s*=\s*)"[^"]*"/m,
      (_m, lead: string) => `${lead}"${projectId}"`
    )
    .replace(
      /^(\s*GOOGLE_APPLICATION_CREDENTIALS\s*=\s*)"[^"]*"/m,
      (_m, lead: string) => `${lead}"${keyFileName}"`
    );
  return next === script ? null : next;
};

export interface PendingKnowledgeSetup {
  keys: KeyFile[];
  /** Which key's project the corpus is built in. */
  corpusKey: number;
  references: File[];
  /** The solution's title, for the corpus's display name. */
  title: string;
}

const pending = new Map<string, PendingKnowledgeSetup>();

export const markPendingKnowledgeSetup = (
  uniqueId: string,
  setup: PendingKnowledgeSetup
) => {
  pending.set(uniqueId, setup);
};

export const hasPendingKnowledgeSetup = (uniqueId: string | undefined) =>
  !!uniqueId && pending.has(uniqueId);

/** Takes (and forgets) the setup for this solution — it runs once. */
export const takePendingKnowledgeSetup = (
  uniqueId: string | undefined
): PendingKnowledgeSetup | null => {
  if (!uniqueId) return null;
  const setup = pending.get(uniqueId) ?? null;
  pending.delete(uniqueId);
  return setup;
};
